import { engineComponent, EngineComponentFactory, Logger, StringUtils } from "neo-common";
import { HtmlElement } from "../HTMLComponents/Elements/HtmlElement";
import { HtmlContainerElement } from "../HTMLComponents/Elements/HtmlContainerElement";
import { HtmlContainerStyle } from "../HTMLComponents/Styles/HtmlContainerStyle";
import { StyleManager } from "../Components/Managers/StyleManager";
import { HtmlManager } from "../Components/Managers/HtmlManager";
import { HtmlController } from "../HTMLComponents/HtmlController";
import { ComponentTypes } from "../Components/ComponentTypes";
import { PlayList } from "../Components/Sounds/PlayList";
import { AppContext } from "../../Common/AppContext";
import { UserControls } from "../Channels/Common/UserControls/UserControls";
import { ModuleManager } from "../Managers/ModuleManager";
import { Languages } from "../Utils/Languages";

/**
 * The HtmlContainerElement class is class which creates holder for other html elements.
 * Can be defined also by Style with class HtmlContainerStyle.
 * Example 1:
 *   <HtmlContainerElement Class="HtmlContainerElement">
 *       <Child1 />
 *       <Child1 />
 *       ...
 *   </HtmlContainerElement>
 *
 * Example 2:
 *   <HtmlContainerElement Class="HtmlContainerElement" Style="HtmlContainerStyle" />
 *
 * @class HtmlContainerElement
 * @extends HtmlElement
 */
@engineComponent(ComponentTypes.HtmlElement)
export class HtmlSvgElement {
    data: string;

    instance: SVGElement;

    childs: Object;

    rid: string;
    ridType: string;
    ridFilter: string;

    private currentValue: string;
    // constructor() {
    //     super('');
    // }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @retutn void
     */
    create(node: Element): void {
        this.instance = document.createElementNS("http://www.w3.org/2000/svg", node.nodeName);

        if (node.childElementCount == 0 && node.textContent != "") {
            this.instance.textContent = node.textContent;
        }

        if (node.hasAttribute("Rid")) {
            this.rid = node.getAttribute("Rid");
            HtmlController.RidElements.push(this);
            this.setText();
        }

        if (node.hasAttribute("Data")) {
            this.data = node.getAttribute("Data");
            HtmlController.registerDataElement(node.getAttribute("Data"), this);
        }

        this.childs = HtmlSvgElement.parseElements(node, this);

        for (let i = 0; i < node.attributes.length; i++) {
            let attribute = node.attributes[i];
            let attributeName = attribute.name;

            if (attributeName == "Rid" || attributeName == "Class") {
                continue;
            }

            switch (attributeName) {
                case "CssClass":
                    attributeName = "class";
                    break;
            }

            this.instance.setAttribute(attributeName, attribute.value);
        }

        // super.create(node);
    }

    getText(): string {
        if (this.currentValue != null) {
            return this.currentValue;
        }

        return null;
    }

    setText(value?: string): void {
        var text: string = "";

        if (value != null) {
            text = value;
            this.currentValue = value;
        }

        if (this.instance != null) {
            // find out rid from languages if element has rid
            if (this.rid != null) {
                if (Languages.getRid(this.rid) != null) {
                    if (!StringUtils.isNullOrEmpty(text)) {
                        var texts: Array<string> = text.split(";");
                        if (texts.length === 2) {
                            text = Languages.getRid(this.rid, texts[0], texts[1]);
                        } else {
                            text = Languages.getRid(this.rid, text);
                        }
                    } else {
                        text = Languages.getRid(this.rid);
                    }
                }
            }

            // format text with ridType
            if (this.ridType != null) {
                text = Languages.formatText(this.ridType, text);
            }

            // filter text based on rid filter
            if (this.ridFilter != null) {
                text = Languages.filterText(this.ridFilter, text);

                if (this.ridFilter === Languages.FilterNoZero && this.instance.classList.contains("bonus")) {
                    if (text === "") {
                        this.instance.classList.add("_hidden");
                    } else {
                        this.instance.classList.remove("_hidden");
                    }
                }
            }

            // set text to instance
            this.instance.innerHTML = text;
        } else {
            if (DEBUG) {
                this.log("ERROR no instance");
            }
        }
    }

    updateText(): void {
        var text: string = "";

        if (this.currentValue != null) {
            text = this.currentValue;
        }

        if (this.instance != null) {
            // find out rid from languages if element has rid
            if (this.rid != null) {
                if (Languages.getRid(this.rid) != null) {
                    if (!StringUtils.isNullOrEmpty(text)) {
                        var texts: Array<string> = text.split(";");
                        if (texts.length === 2) {
                            text = Languages.getRid(this.rid, texts[0], texts[1]);
                        } else {
                            text = Languages.getRid(this.rid, text);
                        }
                    } else {
                        text = Languages.getRid(this.rid);
                    }
                }
            }

            // format text with ridType
            if (this.ridType != null) {
                text = Languages.formatText(this.ridType, text);
            }

            // filter text based on rid filter
            if (this.ridFilter != null) {
                text = Languages.filterText(this.ridFilter, text);

                if (this.ridFilter === Languages.FilterNoZero && this.instance.classList.contains("bonus")) {
                    if (text === "") {
                        this.instance.classList.add("_hidden");
                    } else {
                        this.instance.classList.remove("_hidden");
                    }
                }
            }

            // set text to instance
            this.instance.innerHTML = text;
        } else {
            if (DEBUG) {
                this.log("ERROR no instance");
            }
        }
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            var compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    private static parseElements(startNode: Element, parent: HtmlSvgElement = null): Object {
        const children: Object = {};

        if (!startNode) {
            return;
        }

        HtmlSvgElement.getNodesList(startNode.firstElementChild, [])
            .reverse()
            .filter(
                (node: Element) =>
                    node.hasAttribute("Class") &&
                    EngineComponentFactory.isTypeOf(node.getAttribute("Class"), ComponentTypes.HtmlElement),
            )
            .forEach((node: Element) => {
                const element: any = EngineComponentFactory.get(node.getAttribute("Class"));
                const root: HTMLElement = document.getElementById("root");
                let customRoot: HTMLElement;

                if (node.hasAttribute("RootId")) {
                    customRoot = document.getElementById(node.getAttribute("RootId"));
                }

                if (node.hasAttribute("Key")) {
                    if (!AppContext.gameDefinition.Gamble && node.getAttribute("Key") === "gamble") {
                        return;
                    }

                    if (Object.getPrototypeOf(element) instanceof UserControls) {
                        ModuleManager.registerChannelModule(node.getAttribute("Key"), element);
                    }

                    // if(element instanceof DesktopBaseUserControls) {
                    //     ModuleManager.registerChannelModule(node.getAttribute('Key'), element);
                    // } else if(element instanceof MobileBaseUserControls) {
                    //     ModuleManager.registerChannelModule(node.getAttribute('Key'), element);
                    // } else if(element instanceof GambleUserControls) {
                    //     ModuleManager.registerChannelModule(node.getAttribute('Key'), element);
                    // } else if(element instanceof DesktopFreespinsUserControls) {
                    //     ModuleManager.registerChannelModule(node.getAttribute('Key'), element);
                    // } else if(element instanceof MobileFreespinsUserControls) {
                    //     ModuleManager.registerChannelModule(node.getAttribute('Key'), element);
                    // }
                }

                if (element.instance != null) {
                    if (parent != null) {
                        parent.instance.appendChild(element.instance);
                    } else {
                        if (customRoot != null) {
                            customRoot.appendChild(element.instance);
                        } else if (root != null) {
                            root.appendChild(element.instance);
                        } else {
                            document.body.appendChild(element.instance);
                        }
                    }
                    element.create(node);
                } else {
                    element.create(node);
                    if (parent != null) {
                        parent.instance.appendChild(element.instance);
                    } else {
                        if (customRoot != null) {
                            customRoot.appendChild(element.instance);
                        } else if (root != null) {
                            root.appendChild(element.instance);
                        } else {
                            document.body.appendChild(element.instance);
                        }
                    }
                }

                children[node.nodeName] = element;
            });

        return children;
    }

    private static getNodesList(node: Element, nodeList: Array<Element>): Array<Element> {
        if (!node) {
            return nodeList;
        }

        if (node.nextElementSibling) {
            nodeList = HtmlSvgElement.getNodesList(node.nextElementSibling, nodeList);
        }

        return nodeList.concat(node);
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/HtmlSvgElement.ts
 **/